import { Row, Col, message, Alert } from "antd";
import Table, { ColumnsType } from "antd/lib/table";
import { AUTH_HEADER_NAME, apis, } from "common/apis";
import { IPickerMappingUpload, IPickerMappingUploadRow } from "common/interface/IPickerUploadScheme";
import { routes } from "common/routes";
import { generateHeaders } from "common/utils";
import { AppPageHeader } from "components/AppPageHeader";
import { AppUpload } from "components/AppUpload";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useDocumentTitle } from "hooks/usePageTitle";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { removeProcessingInventoryAdj, setProcessingInventoryAdj } from "store/franchise/franchiseSlice";

const InventoryAdjUploadPage = () => {
  const { authToken } = useAppSelector((state) => state.app.user);
  const { processingInventoryAdj } = useAppSelector(
    (state) => state.franchiseServices
  );
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [hasErrorRows, setHasErrorRows] = useState(false);

  useEffect(() => {
    if (
      processingInventoryAdj &&
      processingInventoryAdj.key
    ) {
      navigate(`/${routes.FRANCHISE.SAVE_INVENTORY_ADJUSTMENT}`, {});
    }

    return () => {
      message.destroy();
    };
  }, [processingInventoryAdj, navigate]);

  useEffect(() => {
    const failed = processingInventoryAdj?.failedRows?.length;

    if (failed) {
      setHasErrorRows(true);
    }
  }, [processingInventoryAdj?.failedRows]);

  const headers: { [name: string]: string } = {
    ...(authToken && { [AUTH_HEADER_NAME]: authToken }),
  };

  const onUploaded = (data: IPickerMappingUpload) => {
    dispatch(setProcessingInventoryAdj(data));
  };

  const onUploading = () => {
    dispatch(removeProcessingInventoryAdj());
  };

  const { headerMapping } = processingInventoryAdj;
  const headerMappingWithError = headerMapping + ",errors:Errors";

  const generateMemoizedHeaders = useMemo(
    () => generateHeaders(headerMappingWithError),
    [headerMappingWithError]
  );

  const tableHeaders = generateMemoizedHeaders || {};
  const successRows = processingInventoryAdj?.successRows || [];
  const failedRows = processingInventoryAdj?.failedRows || [];
  const successData = successRows?.map((row, i) => ({
    ...row,
    key: `data-success-${i}`,
  }));
  const failedData = failedRows?.map((row, i) => ({
    ...row,
    key: `data-failed-${i}`,
  }));
  const data = [...failedData, ...successData];
  const columns: ColumnsType<
    IPickerMappingUploadRow & { key: string }
  > = [
      ...Object.keys(tableHeaders).map((key) => ({
        key,
        title: tableHeaders[key],
        dataIndex: key,
        render: (
          text: string,
          record: IPickerMappingUploadRow & { key: string }
        ) => {
          if (key === "errors") {
            const errorMessages = record.errors
              ?.map((error) => error.message)
              .join(", ");
            return <>{errorMessages}</>;
          }
          return <>{text}</>;
        },
      })),
    ];

  const addBorderToErrorRows = (
    record: IPickerMappingUploadRow & { key: string }
  ) => {
    if (record.errors && record.errors.length) {
      return "error-row";
    }

    return "";
  };

  useDocumentTitle("Franchise Inventory Adjustment Upload");
  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="Upload Inventory Adjustment" />
        </Col>
      </Row>
      
      <Row>
          <Col lg={16} xs={24}>
            <AppUpload
              uploadUrl={apis.uploadFranchiseMapping}
              headers={headers}
              onUploaded={onUploaded}
              onUploading={onUploading}
              hasErrorRows={hasErrorRows}
            />
          </Col>
        </Row>
      {Object.keys(processingInventoryAdj).length != 0 &&
        !processingInventoryAdj.key && (
          <>
            <Row>
              <Col lg={16} xs={24}>
                <Alert
                  type="error"
                  message="Errors in sheet data."
                  description="Please correct errors and upload again."
                  showIcon
                  banner
                />
              </Col>
            </Row>

            <Row>
              <Table
                size="small"
                columns={columns}
                dataSource={data}
                rowClassName={(record) => addBorderToErrorRows(record)}
                scroll={{ y: 500 }}
                className="table-with-errors"
               />
            </Row>
          </>
        )}
    </>
  );
};

export default InventoryAdjUploadPage;